<template>
  <Dialog
    v-model:visible="isVisible"
    header="Add Crew Type"
    :style="{ width: '50vw', maxWidth: '600px' }"
    :modal="true"
    :closable="true"
    @hide="closeModal"
  >
    <div class="p-4">
      <div class="grid grid-cols-1 gap-6">
        <!-- Name -->
        <div>
          <label
            for="name"
            class="block text-gray-700 mb-2 font-medium"
          >Name *</label>
          <InputText
            id="name"
            v-model="formData.name"
            :disabled="isSaving"
            class="w-full"
            placeholder="Enter crew type name"
          />
        </div>

        <!-- Icon Selection -->
        <div>
          <label
            for="icon"
            class="block text-gray-700 mb-2 font-medium"
          >Icon</label>
          <Dropdown
            id="icon"
            v-model="formData.icon"
            :options="iconOptions"
            option-label="label"
            option-value="value"
            :disabled="isSaving"
            class="w-full"
            placeholder="Select an icon"
            :pt="{
              root: { class: 'w-full' }
            }"
          >
            <template #value="slotProps">
              <div
                v-if="slotProps.value"
                class="flex items-center gap-2"
              >
                <img
                  :src="getIconPath(slotProps.value)"
                  alt=""
                  class="w-6 h-6 object-contain"
                >
                <span>{{ getIconName(slotProps.value) }}</span>
              </div>
              <span v-else>{{ slotProps.placeholder }}</span>
            </template>
            <template #option="slotProps">
              <div class="flex items-center gap-2">
                <img
                  :src="slotProps.option.icon"
                  alt=""
                  class="w-6 h-6 object-contain"
                >
                <span>{{ slotProps.option.label }}</span>
              </div>
            </template>
          </Dropdown>
        </div>

        <!-- Description -->
        <div>
          <label
            for="description"
            class="block text-gray-700 mb-2 font-medium"
          >Description</label>
          <Textarea
            id="description"
            v-model="formData.description"
            :disabled="isSaving"
            class="w-full"
            rows="4"
            placeholder="Enter description"
          />
        </div>

        <!-- Rate and Cost -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label
              for="rate"
              class="block text-gray-700 mb-2 font-medium"
            >Day Rate *</label>
            <InputNumber
              id="rate"
              v-model="formData.rate"
              mode="decimal"
              :min-fraction-digits="2"
              :max-fraction-digits="2"
              :disabled="isSaving"
              class="w-full"
              placeholder="0.00"
            />
          </div>

          <div>
            <label
              for="cost"
              class="block text-gray-700 mb-2 font-medium"
            >Cost *</label>
            <InputNumber
              id="cost"
              v-model="formData.cost"
              mode="decimal"
              :min-fraction-digits="2"
              :max-fraction-digits="2"
              :disabled="isSaving"
              class="w-full"
              placeholder="0.00"
            />
          </div>
        </div>

        <!-- Profit Preview -->
        <div
          v-if="formData.rate && formData.cost"
          class="bg-gray-50 rounded-lg p-4"
        >
          <div class="flex items-center justify-between">
            <span class="text-gray-600">Profit:</span>
            <span class="font-medium text-green-600">{{ formatCurrency(formData.rate - formData.cost) }}</span>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end gap-2">
        <Button
          label="Cancel"
          class="p-button-secondary"
          :disabled="isSaving"
          @click="closeModal"
        />
        <Button
          label="Save"
          severity="secondary"
          raised
          :disabled="isSaving || !isFormValid"
          :loading="isSaving"
          @click="saveCrewType"
        />
      </div>
    </template>
  </Dialog>
  <Toast />
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useToast } from 'primevue/usetoast';
import { createCrewType } from '@/services/crewTypesService';
import { formatCurrency } from '@/composables/formatCurrency';
import { getCrewTypeIconOptions, getCrewTypeIcon } from '@/utils/crewTypeIcons';

const props = defineProps({
    visible: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['update:visible', 'crew-type-created']);

const toast = useToast();
const isSaving = ref(false);

const formData = ref({
    name: '',
    description: '',
    rate: null,
    cost: null,
    icon: null
});

const iconOptions = getCrewTypeIconOptions();

const getIconPath = (key) => {
    return getCrewTypeIcon(key) || null;
};

const getIconName = (key) => {
    const option = iconOptions.find(opt => opt.value === key);
    return option ? option.label : '';
};

const isVisible = computed({
    get: () => props.visible,
    set: (value) => emit('update:visible', value)
});

const isFormValid = computed(() => {
    return formData.value.name && 
           formData.value.name.trim() !== '' &&
           formData.value.rate !== null &&
           formData.value.rate >= 0 &&
           formData.value.cost !== null &&
           formData.value.cost >= 0;
});

const closeModal = () => {
    if (!isSaving.value) {
        resetForm();
        isVisible.value = false;
    }
};

const resetForm = () => {
    formData.value = {
        name: '',
        description: '',
        rate: null,
        cost: null,
        icon: null
    };
};

const saveCrewType = async () => {
    if (!isFormValid.value || isSaving.value) return;

    isSaving.value = true;
    try {
        const crewTypeData = {
            name: formData.value.name.trim(),
            description: formData.value.description?.trim() || null,
            rate: parseFloat(formData.value.rate) || 0,
            cost: parseFloat(formData.value.cost) || 0,
            icon: formData.value.icon || null
        };

        const response = await createCrewType(crewTypeData);
        
        toast.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Crew type created successfully',
            life: 3000
        });

        // Emit event to parent to refresh list and close modal
        emit('crew-type-created', response);
        resetForm();
        isVisible.value = false;
    } catch (error) {
        console.error('[AddCrewTypeModal] Error creating crew type:', error);
        const errorMessage = error.response?.data?.message || 
                            (error.response?.data?.errors ? 
                                Object.values(error.response.data.errors).flat().join(', ') : 
                                'Failed to create crew type');
        toast.add({
            severity: 'error',
            summary: 'Error',
            detail: errorMessage,
            life: 5000
        });
    } finally {
        isSaving.value = false;
    }
};

// Reset form when modal closes
watch(() => props.visible, (newValue) => {
    if (!newValue) {
        resetForm();
    }
});
</script>

<style scoped>
/* Additional styles if needed */
</style>

